import { useRef, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import { Upload, Link as LinkIcon, Trash2, Loader2, ImageIcon } from "lucide-react";

const BACKEND = getBackendUrl();

/**
 * Portfolio moodboard block. Sits at the top of the portfolio detail
 * page. Two input modes: upload from disk OR paste a URL. Backend
 * stores one moodboard per portfolio.
 */
export default function MoodboardCard({ portfolio, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [linkValue, setLinkValue] = useState("");
  const inputRef = useRef(null);
  const url = portfolio.moodboard_url;

  const upload = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Moodboard must be an image.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Moodboard too large (max 8 MB).");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/moodboard`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success("Moodboard uploaded.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Upload failed."));
    } finally {
      setBusy(false);
    }
  };

  const saveLink = async () => {
    if (!linkValue.trim()) {
      toast.error("Paste a URL first.");
      return;
    }
    setBusy(true);
    try {
      await axios.patch(`${API}/studio/portfolios/${portfolio.id}/moodboard-link`, {
        url: linkValue.trim(),
      });
      toast.success("Moodboard linked.");
      setShowLink(false);
      setLinkValue("");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Link failed."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/studio/portfolios/${portfolio.id}/moodboard`);
      toast.success("Moodboard removed.");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e, "Remove failed."));
    } finally {
      setBusy(false);
    }
  };

  const isExternal = portfolio.moodboard_kind === "link";
  const displayUrl = url ? (isExternal ? url : `${BACKEND}${url}`) : "";

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
          <ImageIcon className="w-3 h-3" /> Moodboard
        </div>
        {url && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="moodboard-remove"
            className="text-[10px] text-white/40 hover:text-red-400 inline-flex items-center gap-1 transition"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>

      {url ? (
        <div className="rounded-xl overflow-hidden border border-white/8 bg-black">
          <img
            src={displayUrl}
            alt="Moodboard"
            className="w-full max-h-64 object-cover"
            data-testid="moodboard-image"
            crossOrigin={isExternal ? "anonymous" : undefined}
          />
        </div>
      ) : (
        <div
          className="rounded-xl border border-dashed border-white/15 bg-[#0B0B0B] p-8 text-center"
          data-testid="moodboard-empty"
        >
          <p className="text-sm text-white/70 font-medium">No moodboard yet</p>
          <p className="text-[11px] text-white/40 mt-1">
            Upload an inspiration board from your system or paste a URL (Pinterest, Behance, Dropbox — any image link).
          </p>

          <div className="mt-4 flex items-center justify-center gap-2">
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid="moodboard-file-input"
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              data-testid="moodboard-upload-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
            >
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5" /> Upload from disk</>}
            </button>
            <button
              type="button"
              onClick={() => setShowLink((s) => !s)}
              data-testid="moodboard-link-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/25 transition"
            >
              <LinkIcon className="w-3.5 h-3.5" /> Paste URL
            </button>
          </div>

          {showLink && (
            <div className="mt-3 flex items-center gap-2 max-w-md mx-auto">
              <input
                type="url"
                value={linkValue}
                onChange={(e) => setLinkValue(e.target.value)}
                placeholder="https://…"
                data-testid="moodboard-link-input"
                className="flex-1 bg-[#141414] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white outline-none focus:border-[#D4AF37]/50"
              />
              <button
                type="button"
                onClick={saveLink}
                disabled={busy}
                data-testid="moodboard-link-save"
                className="text-xs px-3 py-1.5 rounded-lg bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
